import { BaseModel, BaseModelProps } from '@mail/3promo';
import { ReactRenderer } from '@mail/3promo/render/react';
import { LocalStorageStore } from '@mail/3promo/stores/localStorage';
import type { ModelCreator } from '@mail/3promo/types';
import modalImage from 'img/baz/voluntarilyBAZPromo.png';
import React from 'react';
import { IS_MOBILE_OR_TABLET_BROWSER, IS_WEBVIEW, IS_WEBVIEW_FROM_UA } from 'reactApp/appHelpers/configHelpers';
import {
    voluntarilyBAZPromoConfig,
    voluntarilyBAZPromoForceTrial,
    VOLUNTARY_BAZ_PROMO_ID,
} from 'reactApp/appHelpers/featuresHelpers/features/voluntarilyBAZ';
import { SPACE_LIST } from 'reactApp/constants/spaceList';
import { BillingDataSelectors } from 'reactApp/modules/billingData/billingData.selectors';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { SettingsSelectors } from 'reactApp/modules/settings/settings.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UpgradeSubscriptionSelectors } from 'reactApp/modules/upgradeSubscription/upgradeSubscription.selectors';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { store as reduxStore } from 'reactApp/store';
import { sendXray } from 'reactApp/utils/ga';

class PopupModel extends BaseModel {
    getParent(): HTMLElement | null {
        return document.documentElement;
    }
}

const LazyComponent = React.lazy(() => import('reactApp/ui/VoluntarilyBAZPromo/VoluntarilyBAZPromo'));

const SuspendedComponent = () => {
    return (
        <React.Suspense fallback={null}>
            <LazyComponent />
        </React.Suspense>
    );
};

export const voluntarilyBAZPromo: ModelCreator = ({ models, render, stores }) => {
    return new PopupModel(
        VOLUNTARY_BAZ_PROMO_ID,
        models.base,
        new BaseModelProps(VOLUNTARY_BAZ_PROMO_ID, {
            store: new LocalStorageStore(VOLUNTARY_BAZ_PROMO_ID, stores.localStorage),
            renderer: new ReactRenderer(VOLUNTARY_BAZ_PROMO_ID, SuspendedComponent, render.react),
            preloadingImages: [modalImage],
            closable: true,
            showLimit: voluntarilyBAZPromoConfig.showLimit,
            closeLimit: voluntarilyBAZPromoConfig.closeLimit,
            showInterval: voluntarilyBAZPromoConfig.showInterval,
            templateData: {},
            ignoreTestOnRedraw: true,
            ignorePromoShowInterval: true,
            test: (resolve, reject) => {
                const state = reduxStore.getState();
                const storage = getCurrentStorage(state);

                if (IS_MOBILE_OR_TABLET_BROWSER || IS_WEBVIEW || IS_WEBVIEW_FROM_UA) {
                    reject();
                    return;
                }

                if (storage !== EStorageType.home) {
                    reject();
                    return;
                }

                const { action = '' } = SettingsSelectors.getQueryParams(state);
                if (action) {
                    reject();
                    sendXray(['promo-test', VOLUNTARY_BAZ_PROMO_ID, 'rejectbyaction']);
                    return;
                }

                const isBizUser = UserSelectors.isBizUser(state);
                const isCorpUser = UserSelectors.isCorpUser(state);
                const isPaidUser = UserSelectors.isPaidUser(state);

                const isUpgradeAvaliable = UpgradeSubscriptionSelectors.isUpgradeAvaliable(state);

                const isReject = isBizUser || !isPaidUser || isCorpUser || (!voluntarilyBAZPromoForceTrial && !isUpgradeAvaliable);

                sendXray(['promo-test', VOLUNTARY_BAZ_PROMO_ID, isReject ? 'reject' : 'resolve']);

                if (isReject) {
                    reject();
                    return;
                }

                const subscriptionsBySpace = BillingDataSelectors.sortSubscriptionsBySpace(state);
                const subscription = subscriptionsBySpace[subscriptionsBySpace.length - 1];
                const space = subscription?.space?.original;

                if (space >= SPACE_LIST.gb_256) {
                    reject();
                    return;
                }

                resolve();
            },
        })
    );
};
